package com.dotdrives.web.pages.giving

import com.dotdrives.model.FormConfig
import com.dotdrives.model.FormField
import com.dotdrives.model.displayLabel
import kotlinx.serialization.json.*
import react.FC
import react.Key
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.fieldset
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.legend
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.textarea
import web.cssom.ClassName
import web.html.InputType
import web.html.checkbox
import web.html.email
import web.html.tel
import web.html.number
import web.html.text

internal fun givingContactFields(config: FormConfig): List<FormField> {
    val fields = config.fields.toMutableList()
    if (fields.none { it.key in listOf("name", "full_name", "first_name", "last_name") }) {
        fields.add(FormField("full_name", "text", required = true, order = -1))
    }
    if (fields.none { it.key == "email" }) {
        fields.add(FormField("email", "email", required = true, order = Int.MAX_VALUE))
    }
    return fields
        .map { if (it.key == "email") it.copy(required = true, type = "email") else it }
        .sortedBy { it.order }
}

internal val GivingContactFields =
    FC<GivingSectionProps> { props ->
        val state = props.state
        val controller = props.controller
        val form = state.form ?: return@FC
        fieldset {
            disabled = state.locked
            legend { +"Your details" }
            div {
                className = ClassName("give-contact-grid")
                givingContactFields(form.config).forEach { field ->
                    GivingContactField {
                        key = field.key.unsafeCast<Key>()
                        this.field = field
                        this.state = state
                        this.controller = controller
                    }
                }
            }
            if (form.config.toggles.anonymousGifts)
                label {
                    className = ClassName("give-check")
                    input {
                        type = InputType.checkbox
                        checked = state.anonymous
                        onChange = { controller.setAnonymous(it.currentTarget.checked) }
                    }
                    span { +"Keep my name private in giving feeds" }
                }
        }
    }

private external interface GivingContactFieldProps : GivingSectionProps {
    var field: FormField
}

private val GivingContactField =
    FC<GivingContactFieldProps> { props ->
        val field = props.field
        val state = props.state
        val controller = props.controller
        val title = field.displayLabel
        val fieldLabel = title + if (field.required) " *" else ""
        val currentValue =
            when (field.key) {
                "email" -> state.email
                "name",
                "full_name" -> state.name
                else -> state.fields[field.key]?.jsonPrimitive?.content.orEmpty()
            }
        fun change(value: String) {
            when (field.key) {
                "email" -> controller.setEmail(value)
                "name",
                "full_name" -> controller.setName(value)
                else -> controller.setField(field.key, JsonPrimitive(value))
            }
        }
        label {
            className =
                ClassName(
                    when {
                        field.type == "checkbox" -> "give-check"
                        field.key in listOf("first_name", "last_name") ->
                            "give-field give-name-field"
                        else -> "give-field"
                    }
                )
            if (field.type == "checkbox") {
                input {
                    type = InputType.checkbox
                    ariaLabel = fieldLabel
                    required = field.required
                    checked = state.fields[field.key]?.jsonPrimitive?.booleanOrNull ?: false
                    onChange = {
                        controller.setField(field.key, JsonPrimitive(it.currentTarget.checked))
                    }
                }
                span { +fieldLabel }
            } else {
                span { +fieldLabel }
                when {
                    field.options != null ->
                        select {
                            ariaLabel = fieldLabel
                            value = currentValue
                            required = field.required
                            onChange = { change(it.currentTarget.value) }
                            option {
                                value = ""
                                +"Choose an option"
                            }
                            field.options.orEmpty().forEach { choice ->
                                option {
                                    value = choice
                                    +choice
                                }
                            }
                        }
                    field.type == "textarea" ->
                        textarea {
                            ariaLabel = fieldLabel
                            value = currentValue
                            required = field.required
                            maxLength = 2000
                            onChange = { change(it.currentTarget.value) }
                        }
                    else ->
                        input {
                            type =
                                when (field.type) {
                                    "email" -> InputType.email
                                    "tel" -> InputType.tel
                                    "number" -> InputType.number
                                    else -> InputType.text
                                }
                            if (field.type == "number") asDynamic().step = "any"
                            ariaLabel = fieldLabel
                            required = field.required
                            value = currentValue
                            maxLength = 2000
                            onChange = { change(it.currentTarget.value) }
                        }
                }
            }
        }
    }
